import { get } from 'lodash';
import { IM_PAYING_MYSELF } from '@zocdoc-frontend-common/components-insuranceform';
import { reset as resetPhoneVerification } from '@zocdoc-frontend-common/components-phoneverification/Actions';
import { fetchServiceTypes } from '@zocdoc-frontend-common/components-phoneverification/PhoneActions';
import { getUser } from '@zocdoc-frontend-common/state-user-account/actions/user';
import { SCANNED_CARD_FETCH_INITIAL_PAUSE } from 'src/Booking/actions/constants';
import dispatchPageLoadTime from '../../../config/dispatchPageLoadTime';
import { fetchInitialBookingData } from '../../actions/InitialBookingActions';
import {
    setScannedCardLoadingState,
    setScannedCardFetchRetryCount,
    handleScanInsuranceCardSuccess,
    fetchAndSetInsuranceCards,
    setScannedCardScannerOpenState,
    setFetchScannedCardError,
    setIsFetchingInsuranceCards,
} from '../../actions/InsuranceCardActions';
import {
    updateBookingState,
    updateBookingRequirementValue,
} from '../../actions/SchedulingActions';
import {
    CORONA_SCREEN_SYMPTOMS,
    CORONA_SCREEN_CONTACTS,
    CORONA_SCREEN_LOCATION,
} from '../../components/AppointmentRequirements/constants';
import {
    showModal,
    hideModal,
} from '../../components/BookingContainer/BookingContainerActions';
import { reset as resetNewSubpatientForm } from '../../components/NewSubpatientForm/NewSubpatientFormActions';
import { BookingStateProperties } from '../../constants';
import appointmentInfoMapDispatchToProps from '../../pages/AppointmentInfo/mapDispatchToProps';
import selectPatientMapDispatchToProps from '../../pages/SelectPatient/mapDispatchToProps';
import { wrapWithDispatch } from '../../utils';
import { insuranceValidForCheckingAcceptance } from '../../utils';
import * as AppointmentDetailsActions from '../AppointmentDetails/AppointmentDetailsActions';
import appointmentDetailsMapDispatchToProps from '../AppointmentDetails/mapDispatchToProps';
import { showInsurancePickerError } from '../AppointmentInfo/AppointmentInfoPageActions';
import {
    APPOINTMENT_INFO_STRINGS,
    buildResourceConstraintMessage,
} from '../AppointmentInfo/constants';
import {
    hideLoadingAnimation,
    ReviewAndBookPageTrackingActionCreators,
    showLoadingAnimation,
    validateSelectPatient,
    fetchSurveyAction,
    trackViewedInsurancePickerError,
} from './actions';
import submitIfValid from './submitIfValid';

const mapDispatchToProps = (dispatch, ownProps) => ({
    ...selectPatientMapDispatchToProps(dispatch, ownProps),
    ...appointmentInfoMapDispatchToProps(dispatch, ownProps),
    appointmentDetails: appointmentDetailsMapDispatchToProps(
        dispatch,
        ownProps
    ),
    fetchInsuranceInfo: (carrierId, planId) => {
        dispatch(AppointmentDetailsActions.getInsuranceCarrierName(carrierId));
        dispatch(
            AppointmentDetailsActions.getInsurancePlanName(carrierId, planId)
        );
    },
    fetchServiceTypes: () => {
        dispatch(fetchServiceTypes());
    },
    setIsFetchingInsuranceCards: (isFetching) => {
        dispatch(setIsFetchingInsuranceCards(isFetching));
    },
    showLoadingAnimation: () => {
        dispatch(showLoadingAnimation());
    },
    hideLoadingAnimation: () => {
        dispatch(hideLoadingAnimation());
    },
    fetchInitialBookingData: (bookingId) => {
        dispatch(fetchInitialBookingData(bookingId));
    },
    fetchSurveyData: (bookingId) => {
        dispatch(fetchSurveyAction(bookingId));
    },
    _onPatientSelectionSuccess: ({ chosenPatientId, bookingState }) => {
        dispatch(validateSelectPatient());
        if (
            bookingState[BookingStateProperties.patientId] !== chosenPatientId
        ) {
            dispatch(
                updateBookingState({
                    ...bookingState,
                    [BookingStateProperties.patientId]: chosenPatientId,
                })
            );
        }
    },
    _onPhoneVerified: (bookingState) => {
        dispatch(updateBookingState(bookingState));
        dispatch(fetchServiceTypes());
        dispatch(resetPhoneVerification());
    },
    onBookAppointmentClick: (bookingState, insuranceId, imgSrc) => {
        if (
            insuranceValidForCheckingAcceptance(bookingState) ||
            bookingState[BookingStateProperties.insuranceCarrierId] ===
                IM_PAYING_MYSELF
        ) {
            dispatch(submitIfValid(ownProps.params.bookingId));
        } else {
            dispatch(showInsurancePickerError());
            dispatch(trackViewedInsurancePickerError());
        }

        const insuranceCarrierId = get(
            bookingState,
            [BookingStateProperties.insuranceCarrierId],
            null
        );
        const insurancePlanId = get(
            bookingState,
            [BookingStateProperties.insurancePlanId],
            null
        );
        const insuranceCarrierIdInt =
            insuranceCarrierId === null
                ? insuranceCarrierId
                : parseInt(insuranceCarrierId, 10);
        const insurancePlanIdInt =
            insurancePlanId === null
                ? insurancePlanId
                : parseInt(insurancePlanId, 10);

        const insuranceIdLength = insuranceId?.length || 0;
        const hasInsuranceId = insuranceIdLength > 0;

        const eventDetails = {
            insurance_carrier_id: insuranceCarrierIdInt,
            insurance_plan_id: insurancePlanIdInt,
            has_member_id: hasInsuranceId,
            member_id_character_count: insuranceIdLength,
            has_shared_card_images: Boolean(
                imgSrc &&
                    bookingState[BookingStateProperties.isInsuranceCardShared]
            ),
        };

        dispatch(
            AppointmentDetailsActions.trackClickBookAppointment(eventDetails)
        );
        dispatch(AppointmentDetailsActions.trackBookingApptClicked());
    },
    resetPhoneVerificationForm: () => {
        dispatch(resetPhoneVerification());
    },
    resetNewSubpatientForm: () => {
        dispatch(resetNewSubpatientForm());
    },
    showResourceConstraintModal: ({
        professionalName,
        bookAppointment,
        changeTimeslot,
    }) => {
        dispatch(
            showModal({
                descriptionText:
                    buildResourceConstraintMessage(professionalName),
                primaryButtonText:
                    APPOINTMENT_INFO_STRINGS.ResourceConstraintOkButtonText,
                secondaryButtonText:
                    APPOINTMENT_INFO_STRINGS.ResourceConstraintCancelButtonText,
                onClickPrimaryButton: () => {
                    bookAppointment();
                    dispatch(hideModal());
                },
                onClickSecondaryButton: () => {
                    if (changeTimeslot) changeTimeslot();
                    dispatch(hideModal());
                },
            })
        );
    },
    dispatchPageLoadTime: (component) =>
        dispatch(dispatchPageLoadTime(component)),
    trackExposedControls: () => {
        dispatch(AppointmentDetailsActions.trackExposedControls());
    },
    // coronavirus requirement values
    updateCoronaRequirementsValues: ({ symptoms, contacts, location }) => {
        if (symptoms !== null) {
            dispatch(
                updateBookingRequirementValue(
                    CORONA_SCREEN_SYMPTOMS,
                    !!symptoms
                )
            );
        }
        if (contacts !== null) {
            dispatch(
                updateBookingRequirementValue(
                    CORONA_SCREEN_CONTACTS,
                    !!contacts
                )
            );
        }
        if (location !== null) {
            dispatch(
                updateBookingRequirementValue(
                    CORONA_SCREEN_LOCATION,
                    !!location
                )
            );
        }
    },
    getUser: () => {
        dispatch(getUser());
    },
    onScanInsuranceCardImagesSuccess: (correlationId) => {
        dispatch(setScannedCardLoadingState(true));
        dispatch(handleScanInsuranceCardSuccess(correlationId));
        dispatch(setScannedCardFetchRetryCount(0));
        dispatch(setFetchScannedCardError(false));
        setTimeout(
            () => dispatch(fetchAndSetInsuranceCards(true)),
            SCANNED_CARD_FETCH_INITIAL_PAUSE
        );
    },
    setScannedCardScannerOpenState: (isScannerOpen) => {
        dispatch(setScannedCardScannerOpenState(isScannerOpen));
    },
    setScannedCardLoadingState: (isLoading) => {
        dispatch(setScannedCardLoadingState(isLoading));
    },
    ...wrapWithDispatch(ReviewAndBookPageTrackingActionCreators, dispatch),
});

export default mapDispatchToProps;
